import React, { useState, useMemo } from 'react';

import { SocialButtons } from '../social';
import { closex, menu, TVGuideIcon } from '../icon';
import { SideNav } from './sideNav';
import { SearchBar } from './SearchBar';
import { Cart } from './Cart';
import cx from 'classnames';

import {
  useLockBodyScrollOnValue,
  NavigationLinks,
  Link,
  useMediaQuery,
  useScrollDirection,
  ScrollDirection,
  ICE_URL,
  SPLITIO_KEY,
  useSplitIO,
} from '@sky-tv-group/shared';

import { User } from './User';

const HEADER_HEIGHT = '4.5rem';
const MENUS_TO_REMOVE = ['Offers', 'Get Sky', 'TV Products', 'Broadband'];

interface BtnProps {
  SessionClickHandler: () => void;
  isSignedIn: boolean;
}

let LoginButton = ({
  SessionClickHandler,
  isSignedIn,
  ...rest
}: BtnProps & React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>) => {
  return (
    <button onClick={() => SessionClickHandler()} {...rest}>
      {isSignedIn ? 'Log out' : 'Log In'}
    </button>
  );
};

interface Props {
  SessionClickHandler?: () => void;
  isSignedIn?: boolean;
  name?: string;
  links: NavigationLinks;
  cartURL?: string;
  cartSummary?: JSX.Element | null;
}

const removeOfferFromMenu = (menus: NavigationLinks, isSignedIn: boolean | undefined) => {
  return {
    ...menus,
    header: menus.header.filter((item: Link) => !(MENUS_TO_REMOVE.includes(item?.title ?? '') && isSignedIn)),
    side: menus.side.filter((item: Link) => !(MENUS_TO_REMOVE.includes(item?.title ?? '') && isSignedIn)),
  };
};

let Header = ({ SessionClickHandler, name, isSignedIn, links, cartURL, cartSummary }: Props) => {
  let currentUrl = window.location.href;
  const [isMenuVisible] = useSplitIO(SPLITIO_KEY.WEB_ENHANCE_FOR_LOGIN_CUSTOMER);

  const mutatedLink: NavigationLinks = useMemo(() => {
    if (typeof isSignedIn === 'undefined') {
      return links; // Don't mutate until auth is known
    }
    return removeOfferFromMenu(links, isMenuVisible ? isSignedIn : false);
  }, [links, isSignedIn]);

  const [isSearchMenuVisible] = useSplitIO(SPLITIO_KEY.SKYWEB_SEARCH_MENU);

  let [showSidebar, showSideBarSet] = useState(false);
  let close = () => showSideBarSet(false);
  const isMobile = useMediaQuery('(max-width: 480px)');
  const iceWebMobile = isMobile && cartURL === ICE_URL;
  const scrollDirection = useScrollDirection(iceWebMobile);
  const headerClassNames = useMemo(
    () =>
      cx(
        'bg-primary-white flex items-center justify-start z-100 px-20p md:px-25px 2xl:px-50px shadow-md sticky top-0 transition-all delay-500 duration-200 ease-out',
        { '-top-5': iceWebMobile && scrollDirection === ScrollDirection.DOWN }
      ),
    [iceWebMobile, scrollDirection]
  );

  useLockBodyScrollOnValue(showSidebar);

  return (
    <>
      {showSidebar && (
        <div className="fixed inset-0 z-1000">
          <div className="absolute inset-0 bg-primary-mid-night-blue bg-opacity-50" onClick={close}></div>
          <div className="absolute h-full left-0 w-110 md:w-120 max-w-full bg-primary-white">
            <div className="px-3 md:px-6">
              <button className="w-16 h-16 text-primary-mid-night-blue" onClick={close}>
                {closex}
              </button>
            </div>
            <p className="md:hidden text-2xl md:text-link-s text-primary-mid-night-blue font-skyBold px-8 pt-4">
              {isSignedIn ? `Kia Ora, ${name}` : 'Welcome'}
            </p>
            <SideNav
              close={close}
              link={{
                children: mutatedLink.side,
              }}
            />

            <div className=" absolute left-0 bottom-0 w-full">
              <div className="flex justify-center py-5">
                <SocialButtons socialIcons={mutatedLink.social} />
              </div>
              {SessionClickHandler && (
                <div className="sm:hidden flex justify-center bg-primary-bright-blue text-primary-white text-center">
                  <LoginButton
                    className="font-skyBold p-4 text-2xl md:text-link-s text-primary-white hover:text-primary-mid-night"
                    SessionClickHandler={SessionClickHandler}
                    isSignedIn={!!isSignedIn}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Navbar */}
      <div className={headerClassNames} style={{ height: HEADER_HEIGHT }}>
        <div className="flex min-w-16 md:pr-8">
          <div className="flex items-center md:gap-6 2lg:gap-0 px-0 w-full" style={{ height: HEADER_HEIGHT }}>
            {/* Hide hamburger menu from desktop. */}
            <div className="2lg:hidden">
              <button
                className="w-18 h-18 relative text-primary-mid-night-blue top-3px"
                onClick={() => showSideBarSet(true)}>
                {menu}
              </button>
            </div>
            <div className="w-full h-full flex items-center">
              <a href={mutatedLink.logoUrl}>
                <img src={mutatedLink.logoSrc} className="sm:h-10 h-32px m-auto" alt="Sky logo"></img>
              </a>
            </div>
          </div>
          {/* <div className="border-angle inline-block"></div> */}
        </div>

        <nav id="header-nav" className="new-navbar">
          {mutatedLink.header.map(link => {
            let current = currentUrl.includes(link.link!);
            return (
              <a
                key={link.title}
                href={link.link}
                className={cx('font-skyBold text-primary-mid-night-blue  leading-normal', {
                  'font-skyBold md:sky-h6-black': current,
                  'font-skyBook font-bold md:sky-h7': !current,
                })}>
                {link.title}
              </a>
            );
          })}
        </nav>

        <div className={`flex items-center gap-6 2lg:gap-4 justify-end 2lg:min-w-64 pl-6 ml-auto`}>
          {iceWebMobile && cartSummary ? (
            cartSummary
          ) : (
            <>
              <a className="flex" href={mutatedLink.TVGuideURL}>
                <TVGuideIcon />
              </a>
              {isSearchMenuVisible && <SearchBar />}

              {/* Put My Account on the right side. */}
              {SessionClickHandler && (
                <div
                  className={`sm:flex items-center border-primary-mid-night-blue px-4 hidden sm:pt-2 ${!isSignedIn &&
                    'border-2 rounded-full '}`}
                  style={{ borderColor: ' #0057FF' }}>
                  <User isSignedIn={isSignedIn} />
                  <div className="flex flex-col ">
                    {isSignedIn && (
                      <>
                        <p className="text-link-xxs text-primary-mid-night-blue font-normal block whitespace-no-wrap">
                          Kia Ora,
                        </p>
                        <p className="text-sm text-primary-mid-night-blue font-bold whitespace-no-wrap"> {name}</p>
                      </>
                    )}
                    <LoginButton
                      className={`text-primary-bright-blue ${
                        isSignedIn ? 'text-sm underline text-left' : 'text-link-m'
                      } block whitespace-no-wrap font-bold sm:pb-2`}
                      style={{ textUnderlineOffset: '4px' }}
                      SessionClickHandler={SessionClickHandler}
                      isSignedIn={!!isSignedIn}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export { Header, HEADER_HEIGHT };
